/**
 * Lift2Fit — coaching workspace: one client (`#/crm/clients/:clientId`).
 *
 * Everything the workspace may see about a linked client, and nothing more:
 * their programme (read and prescribed through the `?clientId=` builder form),
 * their sessions read-only as far as their shared fields allow, their bookings,
 * their payments and the deterministic insight flags with their evidence.
 *
 * When the link is gone the API answers 404 and this screen says so — it never
 * pretends to a broader view.
 */
import { useEffect, useState } from 'react';
import {
  api,
  type CrmBooking,
  type CrmClientInsight,
  type CrmPayment,
  type InternalCoach,
  type PlanHistoryEntry,
  type TraineeDetail,
} from '@/lib/api';
import { cn, formatCompactMoney, formatDate, timeAgo } from '@/lib/format';
import { useUnitPreference } from '@/lib/useUnitPreference';
import { useSession } from '@/lib/useSession';
import { AlertIcon, ArrowRightIcon, CheckIcon, SparkIcon } from '@/lib/icons';
import {
  AdherenceBar,
  AppLinkButton,
  CollapsedNote,
  EmptyState,
  FlagChips,
  LoadingPanel,
  MetricTile,
  Panel,
  PersonaBadge,
  ScreenHeader,
  StatusChip,
  TrackBadge,
} from '../components';
import { Button } from '@/components/ui';
import { trackMeta } from '../demoData';
import { useAsync } from '../hooks';
import { Link } from '../router';
import { NewBlockPanel, utcToday, type NewBlockChoice } from '../new-block';
import { ScheduledBlockBanner } from '../scheduled-block';
import { ClientProgrammePanel } from './ClientProgrammePanel';
import { ClientSessionsPanel } from './ClientSessionsPanel';
import { CRM_EYEBROW, bookingStatusMeta, crmFlagLabel, formatMoney, formatSlotRange, insightSourceLabel, paymentStatusMeta, severityTone, severityWord, slotKindLabel, managedBySentence } from './shared';

/* ---------------------------------------------------------------------------
   Shared session summary (read-only, field-gated)
   ------------------------------------------------------------------------ */

function SharedSummary({ client, unit }: { client: TraineeDetail; unit: 'metric' | 'imperial' }) {
  const sessions = client.sharedFields.includes('sessions');
  const sets = client.sharedFields.includes('sets');
  const pain = client.sharedFields.includes('pain');
  const bodyweight = client.sharedFields.includes('bodyweight');
  const notes = client.sharedFields.includes('notes');
  const value = (shared: boolean, render: () => string) => (shared ? render() : 'Not shared');

  return (
    <Panel
      testId="crm-client-sessions"
      title="Sessions (read-only)"
      subtitle="Only the fields this client shares. An unshared field cannot appear here."
      actions={<FlagChips flags={client.flags} />}
    >
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {(
          [
            ['Sessions / 7d', value(sessions, () => String(client.sessionsLast7))],
            ['Last session', value(sessions, () => (client.lastSessionAt ? timeAgo(client.lastSessionAt) : 'Never logged'))],
            ['Streak', value(sessions, () => `${client.currentStreakWeeks} wk`)],
            ['Compliance', value(sets, () => (client.compliancePct === null ? '—' : `${client.compliancePct}%`))],
            [
              'Volume / 7d',
              value(sets, () => {
                if (client.avgs.volumeKg7 === null) return '—';
                const kg = client.avgs.volumeKg7;
                const shown = unit === 'imperial' ? Math.round(kg * 2.20462) : Math.round(kg);
                return `${shown.toLocaleString('en-GB')} ${unit === 'imperial' ? 'lb' : 'kg'}`;
              }),
            ],
            ['Avg RPE / 7d', value(sets, () => (client.avgs.avgRpe7 === null ? '—' : String(client.avgs.avgRpe7)))],
            ['Pain / 14d', value(pain, () => (client.avgs.avgPain14 === null ? '—' : `${client.avgs.avgPain14} / 10`))],
            ['Bodyweight', value(bodyweight, () => (client.bodyweightKg === null ? '—' : `${client.bodyweightKg} kg`))],
          ] as Array<[string, string]>
        ).map(([label, entry]) => (
          <div key={label} className="flex flex-col gap-1 border border-hairline bg-surface p-3">
            <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">{label}</dt>
            <dd className="l2f-tabular font-mono text-[15px] font-bold text-ink">{entry}</dd>
          </div>
        ))}
      </dl>

      {notes ? (
        client.recentNotes.length ? (
          <ul className="flex flex-col gap-2">
            {client.recentNotes.map((note) => (
              <li key={note.sessionId} className="border-b border-hairline pb-2 last:border-b-0">
                <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-ink-muted">
                  {note.label ?? 'Session'} · {note.startedAt ? formatDate(note.startedAt) : '—'}
                </p>
                <CollapsedNote id={note.sessionId} note={note.notes} label="Read note" />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[13px] text-ink-muted">No session notes shared yet.</p>
        )
      ) : (
        <p className="text-[13px] text-ink-muted">Session notes are not shared with you.</p>
      )}
    </Panel>
  );
}

/* ---------------------------------------------------------------------------
   The screen
   ------------------------------------------------------------------------ */

function InsightFlagsPanel({ insight }: { insight: CrmClientInsight | null }) {
  const flags = insight?.flags ?? [];
  return (
    <Panel
      testId="crm-client-insights"
      title="Insight flags"
      subtitle="Why the workspace is flagging this client — the evidence and the suggested next step."
      actions={
        <Link
          to="/crm/insights"
          className="inline-flex h-9 items-center gap-2 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
        >
          All insights
          <ArrowRightIcon />
        </Link>
      }
    >
      {flags.length ? (
        <ul className="flex flex-col gap-3">
          {flags.map((flag) => (
            <li
              key={flag.code}
              data-testid={`crm-client-flag-${flag.code}`}
              className="flex flex-col gap-1.5 border border-hairline bg-surface p-3"
            >
              <span className="flex flex-wrap items-center gap-2">
                <StatusChip tone={severityTone(flag.severity)} icon={<AlertIcon />}>
                  {severityWord(flag.severity)}
                </StatusChip>
                <span className="text-[14px] font-bold text-ink">{crmFlagLabel(flag.code)}</span>
                <StatusChip tone="neutral">{insightSourceLabel(flag.source)}</StatusChip>
              </span>
              <p className="text-[13px] text-ink">{flag.evidence}</p>
              <p className="text-[13px] text-ink-muted">
                <span className="font-bold uppercase tracking-[0.06em] text-accent">Next: </span>
                {flag.suggestedAction}
              </p>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          title="Nothing flagged for this client"
          body="No fatigue, adherence, pain or review signal in the current window. The matrix is re-read on every visit."
          icon={<SparkIcon className="h-8 w-8 text-success" />}
        />
      )}
      {insight && insight.verdictReasons.length ? (
        <div className="border-t border-hairline pt-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">How the matrix reached its verdict</p>
          <ul className="mt-1 flex list-disc flex-col gap-1 pl-5 text-[12px] text-ink-muted">
            {insight.verdictReasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </Panel>
  );
}

function BookingsPanel({ bookings }: { bookings: CrmBooking[] }) {
  return (
    <Panel testId="crm-client-bookings" title="Bookings" subtitle="Their places on your schedule, newest first.">
      {bookings.length ? (
        <ul className="flex flex-col divide-y divide-hairline">
          {bookings.map((booking) => {
            const meta = bookingStatusMeta(booking.status);
            return (
              <li key={booking.bookingId} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <span className="flex min-w-0 flex-col">
                  <span className="text-[13px] font-bold text-ink">
                    {formatDate(booking.slot.startsAt)} · {formatSlotRange(booking.slot.startsAt, booking.slot.endsAt)}
                  </span>
                  <span className="text-[12px] text-ink-muted">
                    {slotKindLabel(booking.slot.kind)} · {booking.slot.location ?? 'no location'} · {booking.slot.booked}/
                    {booking.slot.capacity} places
                  </span>
                </span>
                <StatusChip tone={meta.tone}>{meta.label}</StatusChip>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState
          title="No bookings yet"
          body="Book them from the calendar and their places appear here."
          icon={<AlertIcon className="h-8 w-8 text-accent" />}
          action={
            <AppLinkButton to="/crm/calendar" variant="outline">
              Open the calendar
            </AppLinkButton>
          }
        />
      )}
    </Panel>
  );
}

function PaymentsPanel({ payments, currency }: { payments: CrmPayment[]; currency: string }) {
  const totals = payments.reduce(
    (sum, payment) => {
      if (payment.status === 'paid') sum.paid += payment.amount;
      else if (payment.status === 'due') sum.due += payment.amount;
      else sum.refunded += payment.amount;
      return sum;
    },
    { paid: 0, due: 0, refunded: 0 },
  );
  return (
    <Panel testId="crm-client-payments" title="Payments" subtitle="What this client has been billed in the ledger.">
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricTile label="Paid" value={formatCompactMoney(totals.paid, currency)} valueTitle={formatMoney(totals.paid, currency)} hintTone="success" />
        <MetricTile
          label="Due"
          value={formatCompactMoney(totals.due, currency)}
          valueTitle={formatMoney(totals.due, currency)}
          hintTone={totals.due > 0 ? 'warning' : 'success'}
        />
        <MetricTile label="Refunded" value={formatCompactMoney(totals.refunded, currency)} valueTitle={formatMoney(totals.refunded, currency)} />
      </dl>
      {payments.length ? (
        <ul className="flex flex-col divide-y divide-hairline">
          {payments.map((payment) => {
            const meta = paymentStatusMeta(payment.status);
            return (
              <li key={payment.paymentId} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <span className="flex min-w-0 flex-col">
                  <span className="text-[13px] font-bold text-ink">
                    {formatMoney(payment.amount, payment.currency)}
                    {payment.description ? ` · ${payment.description}` : ''}
                  </span>
                  <span className="text-[12px] text-ink-muted">
                    {payment.periodStart
                      ? `${formatDate(payment.periodStart)}${payment.periodEnd ? ` – ${formatDate(payment.periodEnd)}` : ''}`
                      : 'No period set'}
                    {payment.dueOn ? ` · due ${formatDate(payment.dueOn)}` : ''}
                  </span>
                </span>
                <StatusChip tone={meta.tone}>{meta.label}</StatusChip>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-[13px] text-ink-muted">No payments on this client yet.</p>
      )}
    </Panel>
  );
}

/**
 * 0030: the owner assigns (or reassigns) a client to an internal coach right
 * here on the client page. The client keeps their thread, programme and
 * progress — only `assigned_coach_id` moves.
 */
function AssignClientPanel({
  clientId,
  managedBy,
  coaches,
  onAssigned,
}: {
  clientId: string;
  managedBy: TraineeDetail['managedBy'];
  coaches: InternalCoach[];
  onAssigned: () => void;
}) {
  const [selected, setSelected] = useState(
    managedBy?.kind === 'internal' ? managedBy.coachId : '',
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setSelected(managedBy?.kind === 'internal' ? managedBy.coachId : '');
  }, [managedBy?.kind, managedBy?.coachId]);

  const active = coaches.filter((coach) => coach.status === 'active');

  const save = async () => {
    setBusy(true);
    setMessage(null);
    const result = await api.assignClient(clientId, selected === '' ? null : selected);
    setBusy(false);
    if (result.problem) {
      setMessage(result.problem.detail ?? 'That assignment was refused.');
      return;
    }
    setMessage(
      result.data?.assignedCoachName
        ? `Now managed by ${result.data.assignedCoachName}.`
        : 'Returned to your own management.',
    );
    onAssigned();
  };

  return (
    <Panel
      testId="crm-client-assign"
      title="Who manages this client"
      subtitle="An internal coach sees only the clients you assign them, and never your payments or revenue."
      bodyClassName="gap-3"
    >
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex min-w-[220px] flex-1 flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Manage</span>
          <select
            value={selected}
            onChange={(event) => setSelected(event.target.value)}
            data-testid="client-assign-select"
            className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
          >
            <option value="">You (the owner)</option>
            {active.map((coach) => (
              <option key={coach.coachId} value={coach.coachId}>
                {coach.displayName}
              </option>
            ))}
          </select>
        </label>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => void save()}
          data-testid="client-assign-save"
        >
          Save
        </Button>
      </div>
      {active.length === 0 ? (
        <p className="text-[12px] text-ink-muted">
          No internal coaches yet — invite one from Profile &amp; settings.
        </p>
      ) : null}
      {message ? (
        <p className="text-[13px] text-ink-muted" data-testid="client-assign-message">
          {message}
        </p>
      ) : null}
    </Panel>
  );
}

export function CrmClientScreen({ clientId, planId }: { clientId: string; planId?: string }) {
  const { unit } = useUnitPreference();
  const session = useSession();
  const detailState = useAsync(() => api.coachTrainee(clientId), [clientId]);
  const programmeState = useAsync(() => api.programmeOverview(null, clientId), [clientId]);
  const historyState = useAsync(() => api.programmePlans(clientId), [clientId]);

  const [blockBusy, setBlockBusy] = useState(false);
  const [blockMessage, setBlockMessage] = useState<string | null>(null);
  const [newBlockOpen, setNewBlockOpen] = useState(false);
  const [newBlockError, setNewBlockError] = useState<string | null>(null);
  /**
   * The block the coach is looking at. Null means the active block; All
   * Programmes can open a draft directly, and a just-created block opens here
   * so it can be edited before it is ever activated.
   */
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(planId ?? null);

  /**
   * New Block for a client: the same panel the lifter uses, written through the
   * coach scope so the block is coach-owned (read-only on the client's side).
   *
   * A coach's block is a PRESCRIPTION, never a swap (see A/B): "start now" lands
   * it inactive, dated today, and it is the lifter who starts it — so their
   * current block keeps running and the coach's page shows the new block as
   * "prescribed — not currently active".
   */
  const createBlockForClient = async (choice: NewBlockChoice) => {
    setNewBlockError(null);
    setBlockBusy(true);
    setBlockMessage(null);
    const startNow =
      choice.when === 'now' ||
      (choice.when === 'date' && choice.startOn !== null && choice.startOn <= utcToday());
    const when = {
      startNow,
      startOn: choice.when === 'date' && !startNow ? choice.startOn : null,
    };
    const created =
      choice.mode === 'copy' && choice.copyFromPlanId
        ? await api.copyProgrammePlan(choice.copyFromPlanId, choice.name, clientId, when)
        : await api.createProgrammePlan({
            name: choice.name,
            copyFromPlanId: null,
            clientId,
            ...when,
          });
    setBlockBusy(false);
    if (created.problem || !created.data) {
      setNewBlockError(created.problem?.detail ?? 'The block could not be created.');
      return;
    }
    setNewBlockOpen(false);
    setSelectedPlanId(created.data.planId);
    setBlockMessage(
      startNow
        ? 'New block saved for the client. Nothing changed in their active slot — they start it when they are ready, from the banner on their Programme page.'
        : choice.when === 'date'
          ? `Scheduled for ${choice.startOn}. It is saved, not running — the client starts it when they are ready.`
          : 'Saved as a draft. It waits in All Programmes until the client starts it.',
    );
    historyState.reload();
    programmeState.reload();
  };

  /** A saved future block is started only by this explicit action (0041). */
  const activateScheduled = async (entry: PlanHistoryEntry) => {
    setBlockBusy(true);
    setBlockMessage(null);
    const result = await api.activateProgramme(entry.planId, clientId);
    setBlockBusy(false);
    if (result.problem) {
      setBlockMessage(result.problem.detail ?? 'That block could not be activated.');
      return;
    }
    setSelectedPlanId(null);
    setBlockMessage(`${entry.name} is now the client's active block.`);
    historyState.reload();
    programmeState.reload();
  };

  const currentPlan = programmeState.data?.plan ?? null;
  const bookingsState = useAsync(() => api.crmBookings({ clientId }), [clientId]);
  const paymentsState = useAsync(() => api.crmPayments({ clientId }), [clientId]);
  const insightsState = useAsync(() => api.crmInsights(), []);
  // 0030: the money scope and the owner's team list (for assignment).
  const summaryState = useAsync(() => api.crmSummary(), []);
  const internalState = useAsync(() => api.internalCoaches(), []);

  const client = detailState.data;
  const overview = programmeState.data;
  const insight = insightsState.data?.data.find((entry) => entry.clientId === clientId) ?? null;
  const canSeeMoney = summaryState.data?.canSeeMoney ?? true;
  const isOwner = session.user?.internalCoachOf == null;

  if (detailState.loading && !client) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingPanel label="Loading the client" rows={3} />
        <LoadingPanel label="Loading their programme" rows={5} />
      </div>
    );
  }

  if (!client) {
    // A linked client who simply has not shared `sessions` is a specific,
    // expected state — name it as that rather than as a failed page load. The
    // API's own sentence stays in the body verbatim.
    const notShared = /does not share/i.test(detailState.error ?? '');
    return (
      <EmptyState
        title={notShared ? 'This client has not shared their sessions' : 'We could not open that client'}
        body={
          detailState.error ??
          'They may have revoked the link. The API is the authority on what this workspace may read.'
        }
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/crm/clients" variant="outline">
            Back to clients
          </AppLinkButton>
        }
      />
    );
  }

  const meta = trackMeta(client.track);
  // 0034: ownership AND delivery — the same sentence the list and chat use.
  const managedLabel = managedBySentence(client.managedBy);

  return (
    <div className="flex flex-col gap-6" data-testid="crm-client">
      <ScreenHeader
        eyebrow={CRM_EYEBROW}
        title={client.displayName}
        badge={<TrackBadge track={client.track} />}
        lead={`${meta.label} · ${client.sharedFields.length} field(s) shared · linked ${
          client.linkedAt ? formatDate(client.linkedAt) : 'recently'
        }${managedLabel ? ` · ${managedLabel}` : ''}.`}
        actions={
          <span className="flex items-center gap-2">
            <PersonaBadge track={client.track} persona={client.coachPersona} />
            {/* The header carries exactly ONE control: back to the roster. The
                two programme actions live in the Programme panel beside the
                block they act on — the same place the lifter's Programme page
                puts New Block — so the title row cannot crowd a laptop screen.
                The nav tab already reaches Clients; this is the small ← that
                gets back without hunting for the tab. */}
            <Link
              to="/crm/clients"
              data-testid="crm-client-back"
              aria-label="Back to clients"
              title="Back to clients"
              className="inline-flex h-9 w-9 items-center justify-center border border-hairline text-[15px] leading-none text-ink hover:border-accent hover:text-accent"
            >
              ←
            </Link>
          </span>
        }
      />

      {client.status === 'revoked' ? (
        <p className="border-l-2 border-warning bg-page-subtle px-3 py-2 text-[13px] text-warning">
          This link has been revoked. They appear as a former client and their data is no longer readable.
        </p>
      ) : null}

      {/* 0030: only the owner assigns; an internal coach sees the manager label
          in the header but has no control here. */}
      {isOwner ? (
        <AssignClientPanel
          clientId={clientId}
          managedBy={client.managedBy}
          coaches={internalState.data?.data ?? []}
          onAssigned={() => detailState.reload()}
        />
      ) : null}

      {newBlockOpen ? (
        <NewBlockPanel
          audience="coach"
          current={currentPlan ? { planId: currentPlan.planId, name: currentPlan.name } : null}
          blocks={historyState.data ?? []}
          busy={blockBusy}
          error={newBlockError}
          onCancel={() => {
            setNewBlockOpen(false);
            setNewBlockError(null);
          }}
          onCreate={(choice) => void createBlockForClient(choice)}
        />
      ) : null}

      {blockMessage && !newBlockOpen ? (
        <p className="border-l-2 border-success bg-page-subtle px-3 py-2 text-[12px] text-ink" data-testid="crm-client-block-message">
          {blockMessage}
        </p>
      ) : null}

      <ScheduledBlockBanner
        blocks={historyState.data ?? []}
        audience="coach"
        busy={blockBusy}
        onActivate={(entry) => void activateScheduled(entry)}
      />

      <ClientProgrammePanel
        clientId={clientId}
        clientName={client.displayName}
        planId={selectedPlanId}
        onSelectPlan={setSelectedPlanId}
        onNewBlock={() => {
          setNewBlockOpen(true);
          setNewBlockError(null);
        }}
      />

      <ClientSessionsPanel
        clientId={clientId}
        clientName={client.displayName}
        sharedFields={client.sharedFields}
        plan={overview?.plan ?? null}
      />

      <SharedSummary client={client} unit={unit} />

      {/* One chat surface, not two. The thread lives in the standalone chat
          workspace (#/chat), where the coach has the client's context beside it;
          this page keeps a single prominent way in rather than a second, partial
          copy of the same conversation. The last message is shown so the coach
          can see at a glance whether there is anything waiting. */}
      <Panel
        testId="crm-client-chat"
        title="Chat"
        subtitle="The conversation lives in the chat workspace, beside this client's context. Nothing is repeated here."
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="min-w-0 flex-1 text-[13px] text-ink-muted" data-testid="crm-client-chat-last">
            {client.displayName}’s thread — open it to read the history and reply.
          </p>
          <a
            href={`#/chat?clientId=${encodeURIComponent(client.userId)}`}
            target="_blank"
            rel="noreferrer"
            data-testid="crm-client-chat-open"
            className="inline-flex h-11 shrink-0 items-center gap-2 border border-accent bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-accent-600 hover:text-white"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.4 8.9 8.9 0 0 1-3.9-.9L3 21l1.9-5.3a8.4 8.4 0 0 1-.9-3.9A8.4 8.4 0 0 1 12.5 3 8.4 8.4 0 0 1 21 11.5Z" />
            </svg>
            Chat
          </a>
        </div>
      </Panel>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BookingsPanel bookings={bookingsState.data?.data ?? []} />
        {/* 0030: no money panel for a caller without money scope. */}
        {canSeeMoney ? (
          <PaymentsPanel
            payments={paymentsState.data?.data ?? []}
            currency={paymentsState.data?.totals.currency ?? 'GBP'}
          />
        ) : null}
      </div>

      <InsightFlagsPanel insight={insight} />

      <section className="flex flex-wrap items-center gap-3" aria-label="Client adherence">
        <AdherenceBar pct={client.compliancePct} />
        <span className="text-[12px] text-ink-muted">
          <span className={cn('l2f-tabular font-mono font-bold text-ink')}>{client.sessionsLast7}</span> sessions in the last 7
          days · last {client.lastSessionAt ? timeAgo(client.lastSessionAt) : 'never'}
        </span>
      </section>
    </div>
  );
}
